<script>
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Draggable from 'vuedraggable';

export default {
  components: {
    SettingsSection,
    NextButton,
    Draggable,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      enabledFields: {
        full_name: true,
        email: true,
        interested_course: true,
        educational_qualification: true,
        phone_number: true,
        country: true,
        city: true,
        bio: true,
      },
      requiredFields: {
        full_name: false,
        email: true,
        interested_course: true,
        educational_qualification: false,
        phone_number: false,
        country: false,
        city: false,
        bio: false,
      },
      orderedFields: [],
      isUpdating: false,
      formResendThreshold: 5, // Default to 5 messages
      cityOptions: [], // Array of { city: string, subAreas: string[] }
    };
  },
  computed: {
    defaultFieldDefinitions() {
      return [
        {
          name: 'full_name',
          key: 'FULL_NAME',
          required: false,
        },
        {
          name: 'email',
          key: 'EMAIL',
          required: true,
        },
        {
          name: 'interested_course',
          key: 'INTERESTED_COURSE',
          required: true,
        },
        {
          name: 'educational_qualification',
          key: 'EDUCATIONAL_QUALIFICATION',
          required: false,
        },
        {
          name: 'phone_number',
          key: 'PHONE_NUMBER',
          required: false,
        },
        {
          name: 'country',
          key: 'COUNTRY',
          type: 'country',
          required: false,
        },
        {
          name: 'city',
          key: 'CITY',
          required: false,
        },
        {
          name: 'bio',
          key: 'BIO',
          required: false,
        },
      ];
    },
    fieldDefinitions() {
      // Return ordered fields if available, otherwise use default order
      return this.orderedFields.length > 0
        ? this.orderedFields
        : this.defaultFieldDefinitions;
    },
  },
  watch: {
    inbox: {
      handler() {
        this.loadConfig();
      },
      immediate: true,
    },
  },
  methods: {
    loadConfig() {
      const config = this.inbox.contact_form_config || {};
      const enabledFieldsConfig = config.enabled_fields || {};
      const requiredFieldsConfig = config.required_fields || {};
      const fieldOrder = config.field_order || [];

      // If config exists, use it; otherwise use defaults (all true)
      if (Object.keys(enabledFieldsConfig).length > 0) {
        // Convert all values to proper booleans
        this.enabledFields = {
          full_name:
            enabledFieldsConfig.full_name === true ||
            enabledFieldsConfig.full_name === 'true' ||
            enabledFieldsConfig.full_name === 1,
          email:
            enabledFieldsConfig.email === true ||
            enabledFieldsConfig.email === 'true' ||
            enabledFieldsConfig.email === 1,
          interested_course:
            enabledFieldsConfig.interested_course === true ||
            enabledFieldsConfig.interested_course === 'true' ||
            enabledFieldsConfig.interested_course === 1,
          educational_qualification:
            enabledFieldsConfig.educational_qualification === true ||
            enabledFieldsConfig.educational_qualification === 'true' ||
            enabledFieldsConfig.educational_qualification === 1,
          phone_number:
            enabledFieldsConfig.phone_number === true ||
            enabledFieldsConfig.phone_number === 'true' ||
            enabledFieldsConfig.phone_number === 1,
          country:
            enabledFieldsConfig.country === true ||
            enabledFieldsConfig.country === 'true' ||
            enabledFieldsConfig.country === 1,
          city:
            enabledFieldsConfig.city === true ||
            enabledFieldsConfig.city === 'true' ||
            enabledFieldsConfig.city === 1,
          bio:
            enabledFieldsConfig.bio === true ||
            enabledFieldsConfig.bio === 'true' ||
            enabledFieldsConfig.bio === 1,
        };
      } else {
        // Default: all fields enabled
        this.enabledFields = {
          full_name: true,
          email: true,
          interested_course: true,
          educational_qualification: true,
          phone_number: true,
          country: true,
          city: true,
          bio: true,
        };
      }

      // Load required fields config
      if (Object.keys(requiredFieldsConfig).length > 0) {
        this.requiredFields = {
          full_name:
            requiredFieldsConfig.full_name === true ||
            requiredFieldsConfig.full_name === 'true' ||
            requiredFieldsConfig.full_name === 1,
          email:
            requiredFieldsConfig.email === true ||
            requiredFieldsConfig.email === 'true' ||
            requiredFieldsConfig.email === 1,
          interested_course:
            requiredFieldsConfig.interested_course === true ||
            requiredFieldsConfig.interested_course === 'true' ||
            requiredFieldsConfig.interested_course === 1,
          educational_qualification:
            requiredFieldsConfig.educational_qualification === true ||
            requiredFieldsConfig.educational_qualification === 'true' ||
            requiredFieldsConfig.educational_qualification === 1,
          phone_number:
            requiredFieldsConfig.phone_number === true ||
            requiredFieldsConfig.phone_number === 'true' ||
            requiredFieldsConfig.phone_number === 1,
          country:
            requiredFieldsConfig.country === true ||
            requiredFieldsConfig.country === 'true' ||
            requiredFieldsConfig.country === 1,
          city:
            requiredFieldsConfig.city === true ||
            requiredFieldsConfig.city === 'true' ||
            requiredFieldsConfig.city === 1,
          bio:
            requiredFieldsConfig.bio === true ||
            requiredFieldsConfig.bio === 'true' ||
            requiredFieldsConfig.bio === 1,
        };
      } else {
        // Default: email and interested_course are required
        this.requiredFields = {
          full_name: false,
          email: true,
          interested_course: true,
          educational_qualification: false,
          phone_number: false,
          country: false,
          city: false,
          bio: false,
        };
      }

      // Load field order
      if (fieldOrder.length > 0) {
        // Create a map of field names to field definitions
        const fieldMap = {};
        this.defaultFieldDefinitions.forEach(field => {
          fieldMap[field.name] = field;
        });

        // Reorder fields based on saved order
        this.orderedFields = fieldOrder
          .map(fieldName => fieldMap[fieldName])
          .filter(field => field !== undefined);

        // Add any fields that weren't in the saved order (for backward compatibility)
        this.defaultFieldDefinitions.forEach(field => {
          if (!fieldOrder.includes(field.name)) {
            this.orderedFields.push(field);
          }
        });
      } else {
        // Use default order
        this.orderedFields = [...this.defaultFieldDefinitions];
      }

      // Load form resend threshold (handle 0 as valid value)
      // Load it the same way as other config values (enabled_fields, required_fields)
      const thresholdConfig = config.form_resend_threshold;
      if (thresholdConfig !== undefined && thresholdConfig !== null) {
        // Value exists in config, use it (0 is valid to disable)
        this.formResendThreshold = Number(thresholdConfig);
      } else {
        // Default to 5 only if the key doesn't exist at all (for new configs)
        this.formResendThreshold = 5;
      }

      // Load city options
      // Handle both snake_case (from backend) and camelCase (from frontend)
      const cityOptionsConfig = config.city_options || [];
      if (cityOptionsConfig.length > 0) {
        // Convert sub_areas to subAreas for frontend consistency
        this.cityOptions = cityOptionsConfig.map(option => ({
          city: option.city || option['city'] || '',
          subAreas: option.sub_areas || option['sub_areas'] || option.subAreas || option['subAreas'] || []
        }));
      } else {
        // Set default city options
        this.cityOptions = this.getDefaultCityOptions();
      }
    },
    getDefaultCityOptions() {
      return [
        {
          city: 'Coimbatore',
          subAreas: ['Gandhipuram', 'Malumichampatti', 'Saravanampatti', 'Thudiyalur'],
        },
        {
          city: 'Tiruppur',
          subAreas: [],
        },
        {
          city: 'Chennai',
          subAreas: ['Vadapalani', 'Velachery', 'Guduvanchery', 'Ambattur', 'Tambaram', 'Poonamallee'],
        },
        {
          city: 'Kanchipuram',
          subAreas: [],
        },
        {
          city: 'Hosur',
          subAreas: [],
        },
        {
          city: 'Trichy',
          subAreas: [],
        },
        {
          city: 'Bangalore',
          subAreas: ['Marathahalli', 'Bommasandra'],
        },
        {
          city: 'Dubai',
          subAreas: ['Al Quasis'],
        },
        {
          city: 'Malaysia',
          subAreas: ['Kuala Lumpur Bricks Field'],
        },
      ];
    },
    async updateContactFormConfig() {
      this.isUpdating = true;
      try {
        // Ensure all values are proper booleans
        const formattedEnabledFields = {};
        Object.keys(this.enabledFields).forEach(key => {
          formattedEnabledFields[key] = Boolean(this.enabledFields[key]);
        });

        const formattedRequiredFields = {};
        Object.keys(this.requiredFields).forEach(key => {
          // Only set required if the field is enabled
          formattedRequiredFields[key] = this.enabledFields[key]
            ? Boolean(this.requiredFields[key])
            : false;
        });

        // Get field order from orderedFields
        const fieldOrder = this.orderedFields.map(field => field.name);

        // Match the structure used by CSAT config
        // Handle form_resend_threshold - 0 is a valid value (disables auto-resend)
        // Always send the threshold value (just like enabled_fields and required_fields are always sent)
        // Use explicit check to handle 0 correctly (don't use || as 0 is falsy)
        const thresholdValue =
          this.formResendThreshold !== undefined &&
          this.formResendThreshold !== null
            ? Number(this.formResendThreshold)
            : 0;

        // Build city options array
        // If city field is enabled, ensure city_options are saved (even if empty, use defaults)
        let cityOptionsArray = this.cityOptions
          .filter(option => option.city && option.city.trim() !== '')
          .map(option => ({
            city: option.city.trim(),
            sub_areas: (option.subAreas || []).filter(subArea => subArea && subArea.trim() !== '').map(subArea => subArea.trim())
          }));
        
        // If city is enabled but no city options configured, use default options
        if (formattedEnabledFields.city && cityOptionsArray.length === 0) {
          cityOptionsArray = this.getDefaultCityOptions().map(option => ({
            city: option.city,
            sub_areas: option.subAreas || []
          }));
        }

        const payload = {
          id: this.inbox.id,
          formData: false,
          contact_form_config: {
            enabled_fields: formattedEnabledFields,
            required_fields: formattedRequiredFields,
            field_order: fieldOrder,
            form_resend_threshold: thresholdValue,
            city_options: cityOptionsArray,
          },
        };

        await this.$store.dispatch('inboxes/updateInbox', payload);

        // Reload inbox data to get updated config
        await this.$store.dispatch('inboxes/get');

        // Reload config from updated inbox prop (will be reactive)
        this.$nextTick(() => {
          this.loadConfig();
        });

        useAlert(this.$t('INBOX_MGMT.CONTACT_FORM_SETTINGS.UPDATE_SUCCESS'));
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('INBOX_MGMT.CONTACT_FORM_SETTINGS.UPDATE_ERROR')
        );
      } finally {
        this.isUpdating = false;
      }
    },
    onDragEnd() {
      // Field order is automatically updated via v-model on orderedFields
      // This method can be used for additional logic if needed
    },
    getFieldLabel(fieldKey) {
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return this.$t(
        `INBOX_MGMT.CONTACT_FORM_SETTINGS.FIELDS.${fieldKey}.LABEL`
      );
    },
    getFieldDescription(fieldKey) {
      // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
      return this.$t(
        `INBOX_MGMT.CONTACT_FORM_SETTINGS.FIELDS.${fieldKey}.DESCRIPTION`
      );
    },
    addCityOption() {
      this.cityOptions.push({
        city: '',
        subAreas: [],
      });
    },
    removeCityOption(index) {
      this.cityOptions.splice(index, 1);
    },
    addSubArea(cityIndex) {
      if (!this.cityOptions[cityIndex].subAreas) {
        this.cityOptions[cityIndex].subAreas = [];
      }
      this.cityOptions[cityIndex].subAreas.push('');
    },
    removeSubArea(cityIndex, subAreaIndex) {
      this.cityOptions[cityIndex].subAreas.splice(subAreaIndex, 1);
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.CONTACT_FORM_SETTINGS.TITLE')"
      :sub-title="$t('INBOX_MGMT.CONTACT_FORM_SETTINGS.SUB_TITLE')"
      :show-border="false"
    >
      <Draggable
        v-model="orderedFields"
        item-key="name"
        handle=".drag-handle"
        ghost-class="ghost-item"
        animation="200"
        class="space-y-4 pb-4"
        @end="onDragEnd"
      >
        <template #item="{ element: field }">
          <div
            class="p-3 rounded-lg border border-n-weak hover:bg-n-alpha-1 transition-colors"
          >
            <div class="flex items-center gap-3 mb-3">
              <div
                class="drag-handle cursor-grab active:cursor-grabbing text-n-slate-9 hover:text-n-slate-12 transition-colors"
              >
                <fluent-icon icon="drag" size="20" />
              </div>
              <input
                :id="`field-${field.name}`"
                v-model="enabledFields[field.name]"
                type="checkbox"
                class="w-4 h-4 text-n-brand border-n-weak rounded focus:ring-n-brand focus:ring-2 cursor-pointer"
              />
              <label :for="`field-${field.name}`" class="flex-1 cursor-pointer">
                <div class="font-medium text-n-slate-12">
                  {{ getFieldLabel(field.key) }}
                </div>
                <div class="text-sm text-n-slate-11 mt-0.5">
                  {{ getFieldDescription(field.key) }}
                </div>
              </label>
            </div>
            <div
              v-if="enabledFields[field.name]"
              class="flex items-center gap-2 ml-11"
            >
              <input
                :id="`required-${field.name}`"
                v-model="requiredFields[field.name]"
                type="checkbox"
                class="w-4 h-4 text-n-brand border-n-weak rounded focus:ring-n-brand focus:ring-2 cursor-pointer"
              />
              <label
                :for="`required-${field.name}`"
                class="text-sm text-n-slate-11 cursor-pointer"
              >
                {{ $t('INBOX_MGMT.CONTACT_FORM_SETTINGS.MAKE_REQUIRED') }}
              </label>
            </div>
            
            <!-- City Options Configuration (only show for city field) -->
            <div
              v-if="field.name === 'city' && enabledFields[field.name]"
              class="mt-4 ml-11 p-4 border border-n-weak rounded-lg bg-n-alpha-black2"
            >
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ $t('INBOX_MGMT.CONTACT_FORM_SETTINGS.CITY_OPTIONS.LABEL') }}
              </label>
              <p class="text-sm text-n-slate-11 mb-4">
                {{ $t('INBOX_MGMT.CONTACT_FORM_SETTINGS.CITY_OPTIONS.DESCRIPTION') }}
              </p>
              
              <div class="space-y-4">
                <div
                  v-for="(option, index) in cityOptions"
                  :key="index"
                  class="p-4 border border-n-weak rounded-lg bg-n-solid-1"
                >
                  <div class="flex items-center gap-3 mb-3">
                    <input
                      v-model="option.city"
                      type="text"
                      :placeholder="$t('INBOX_MGMT.CONTACT_FORM_SETTINGS.CITY_OPTIONS.CITY_PLACEHOLDER')"
                      class="flex-1 px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand"
                    />
                    <button
                      type="button"
                      class="px-3 py-2 text-sm text-n-ruby-9 hover:bg-n-ruby-3 rounded-lg transition-colors"
                      @click="removeCityOption(index)"
                    >
                      {{ $t('INBOX_MGMT.CONTACT_FORM_SETTINGS.CITY_OPTIONS.REMOVE') }}
                    </button>
                  </div>
                  
                  <div class="space-y-2">
                    <label class="block text-xs font-medium text-n-slate-11 mb-2">
                      {{ $t('INBOX_MGMT.CONTACT_FORM_SETTINGS.CITY_OPTIONS.SUB_AREAS_LABEL') }}
                    </label>
                    <div
                      v-for="(subArea, subIndex) in option.subAreas"
                      :key="subIndex"
                      class="flex items-center gap-2"
                    >
                      <input
                        v-model="option.subAreas[subIndex]"
                        type="text"
                        :placeholder="$t('INBOX_MGMT.CONTACT_FORM_SETTINGS.CITY_OPTIONS.SUB_AREA_PLACEHOLDER')"
                        class="flex-1 px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand"
                      />
                      <button
                        type="button"
                        class="px-2 py-2 text-sm text-n-ruby-9 hover:bg-n-ruby-3 rounded-lg transition-colors"
                        @click="removeSubArea(index, subIndex)"
                      >
                        <fluent-icon icon="dismiss" size="16" />
                      </button>
                    </div>
                    <button
                      type="button"
                      class="mt-2 px-3 py-1.5 text-sm text-n-brand hover:bg-n-alpha-2 rounded-lg transition-colors border border-n-weak"
                      @click="addSubArea(index)"
                    >
                      {{ $t('INBOX_MGMT.CONTACT_FORM_SETTINGS.CITY_OPTIONS.ADD_SUB_AREA') }}
                    </button>
                  </div>
                </div>
                
                <button
                  type="button"
                  class="w-full px-4 py-2 text-sm text-n-brand hover:bg-n-alpha-2 rounded-lg transition-colors border border-n-weak border-dashed"
                  @click="addCityOption"
                >
                  {{ $t('INBOX_MGMT.CONTACT_FORM_SETTINGS.CITY_OPTIONS.ADD_CITY') }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </Draggable>

      <div class="pt-6 border-t border-n-weak mt-6">
        <div class="mb-4">
          <label class="block text-sm font-medium text-n-slate-12 mb-2">
            {{
              $t('INBOX_MGMT.CONTACT_FORM_SETTINGS.FORM_RESEND_THRESHOLD.LABEL')
            }}
          </label>
          <p class="text-sm text-n-slate-11 mb-3">
            {{
              $t(
                'INBOX_MGMT.CONTACT_FORM_SETTINGS.FORM_RESEND_THRESHOLD.DESCRIPTION'
              )
            }}
          </p>
          <input
            v-model.number="formResendThreshold"
            type="number"
            min="0"
            class="w-32 px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-n-brand focus:border-n-brand"
          />
          <p class="text-xs text-n-slate-10 mt-2">
            {{
              $t('INBOX_MGMT.CONTACT_FORM_SETTINGS.FORM_RESEND_THRESHOLD.HINT')
            }}
          </p>
        </div>
      </div>

      <div class="pt-4">
        <NextButton
          :label="$t('INBOX_MGMT.CONTACT_FORM_SETTINGS.SAVE_BUTTON')"
          :is-loading="isUpdating"
          @click="updateContactFormConfig"
        />
      </div>
    </SettingsSection>
  </div>
</template>

<style scoped lang="scss">
.ghost-item {
  @apply opacity-50 bg-n-slate-2;
}

.drag-handle {
  @apply flex items-center justify-center;
  touch-action: none;
}
</style>
